import { t } from "@lingui/macro";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { AppState, Platform } from "react-native";
import { fetchQuery, useRelayEnvironment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import {
  FaceIdIcon,
  FingerprintIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { captureNativeSessionPersistenceGeneration } from "@whoosh/relay/src/private/nativeSessionPersistence";

import { SessionCheckerQueryHandlerQueryString } from "../../navigators/SessionChecker/SessionCheckerQueryHandler";
import { SessionCheckerQueryHandlerQuery } from "../../navigators/SessionChecker/__generated__/SessionCheckerQueryHandlerQuery.graphql";
import { useFlags } from "../../utils/launchDarkly";
import {
  isLocallySignedOutAtom,
  isUserAuthenticated_atom,
} from "../Authentication/atoms";
import { sessionAtom, sessionQueryVersionAtom } from "../Session";
import {
  signInWithBiometrics,
  getBiometricUnlockEnabled,
  getBiometricUnlockType,
  isBiometricUnlockAvailable,
} from "./biometricUnlock";
import { getBiometricUnlockLabel } from "./getBiometricUnlockCopy";

const BIOMETRIC_CANCELLATION_ERRORS = [
  "app_cancel",
  "system_cancel",
  "user_cancel",
];

type BiometricSignInButtonProps = {
  autoPrompt?: boolean;
  onComplete: (clubSlug: string) => void;
  onCancel: () => void;
  preferredClubSlug?: string | null;
};

export const BiometricSignInButton = (props: BiometricSignInButtonProps) => {
  const { biometricAuth } = useFlags();
  return biometricAuth === true ? (
    <BiometricSignInButtonContent {...props} />
  ) : null;
};

const BiometricSignInButtonContent = ({
  autoPrompt = false,
  onComplete,
  onCancel,
  preferredClubSlug,
}: BiometricSignInButtonProps) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [biometricType, setBiometricType] =
    useState<Awaited<ReturnType<typeof getBiometricUnlockType>>>(null);
  const environment = useRelayEnvironment();
  const setIsAuthenticated = useSetAtom(isUserAuthenticated_atom);
  const setIsLocallySignedOut = useSetAtom(isLocallySignedOutAtom);
  const setSession = useSetAtom(sessionAtom);
  const incrementSessionQueryVersion = useSetAtom(sessionQueryVersionAtom);
  const hasPromptedRef = useRef(false);
  const mountedRef = useRef(true);
  const isAuthenticatingRef = useRef(false);
  const biometricPromptCancelledRef = useRef(false);
  const { showToast } = useToast();

  const loadAvailability = useCallback(async () => {
    try {
      const [available, enabled, type] = await Promise.all([
        isBiometricUnlockAvailable(),
        getBiometricUnlockEnabled(),
        getBiometricUnlockType(),
      ]);

      setBiometricType(type);
      setIsAvailable(available && enabled);
    } catch {
      setIsAvailable(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void loadAvailability();
    return () => {
      mountedRef.current = false;
    };
  }, [loadAvailability]);

  useEffect(() => {
    let previousAppState = AppState.currentState;

    const subscription = AppState.addEventListener("change", (nextState) => {
      if (nextState === "active" && previousAppState !== "active") {
        if (
          isAuthenticatingRef.current ||
          biometricPromptCancelledRef.current
        ) {
          previousAppState = nextState;
          return;
        }

        hasPromptedRef.current = false;
        setIsLoading(true);
        void loadAvailability();
      }

      previousAppState = nextState;
    });

    return () => subscription.remove();
  }, [loadAvailability]);

  const handleBiometricSignIn = useCallback(async () => {
    isAuthenticatingRef.current = true;
    setIsLoading(true);

    try {
      const restoreResult = await signInWithBiometrics();

      if (!mountedRef.current) return;

      if (!restoreResult.success) {
        if (
          restoreResult.error &&
          BIOMETRIC_CANCELLATION_ERRORS.includes(restoreResult.error)
        ) {
          biometricPromptCancelledRef.current = true;
          onCancel();
        } else {
          showToast({
            description: t`We couldn't complete biometric sign-in. Please try again.`,
            semanticStyle: "error",
            toastId: "biometric-sign-in-error",
          });
        }

        return;
      }

      const generation = captureNativeSessionPersistenceGeneration();
      const data = await fetchQuery<SessionCheckerQueryHandlerQuery>(
        environment,
        SessionCheckerQueryHandlerQueryString,
        {},
        { fetchPolicy: "network-only" }
      ).toPromise();
      if (
        !mountedRef.current ||
        generation === null ||
        generation !== captureNativeSessionPersistenceGeneration()
      )
        return;
      const user = data?.session?.user;
      const memberProfileEdges =
        user?.__typename === "MemberUser"
          ? user.memberProfiles?.edges ?? []
          : [];
      const clubs = memberProfileEdges.flatMap((edge) => {
        const club = edge?.node?.member?.club;
        return club?.slug ? [club] : [];
      });
      const clubSlug =
        clubs.find((club) => club.slug === preferredClubSlug)?.slug ??
        clubs[0]?.slug;

      if (!user || !clubSlug) {
        showToast({
          description: t`We couldn't complete biometric sign-in. Please try again or sign in another way.`,
          semanticStyle: "error",
          toastId: "biometric-sign-in-unavailable",
        });
        return;
      }

      setIsLocallySignedOut(false);
      setSession({
        user: {
          id: user.id,
          email: user.email ?? "",
          username: user.username ?? "",
          clubs: clubs.map((club) => ({
            id: club.id,
            slug: club.slug ?? "",
            name: club.name ?? "",
            logoImage: club.logoImage?.publicId ?? "",
          })),
        },
      });
      setIsAuthenticated(true);
      incrementSessionQueryVersion((current) => current + 1);
      onComplete(clubSlug);
    } catch {
      showToast({
        description: t`We couldn't complete biometric sign-in. Please try again.`,
        semanticStyle: "error",
        toastId: "biometric-sign-in-error",
      });
    } finally {
      isAuthenticatingRef.current = false;
      setIsLoading(false);
    }
  }, [
    environment,
    incrementSessionQueryVersion,
    onCancel,
    onComplete,
    preferredClubSlug,
    setIsAuthenticated,
    setIsLocallySignedOut,
    setSession,
    showToast,
  ]);

  useEffect(() => {
    if (
      Platform.OS !== "web" &&
      autoPrompt &&
      !isLoading &&
      isAvailable &&
      !hasPromptedRef.current
    ) {
      hasPromptedRef.current = true;
      void handleBiometricSignIn();
    }
  }, [autoPrompt, handleBiometricSignIn, isAvailable, isLoading]);

  if (Platform.OS === "web" || isLoading || !isAvailable) {
    return null;
  }

  const label = getBiometricUnlockLabel(Platform.OS, biometricType);
  const Glyph = biometricType === "face" ? FaceIdIcon : FingerprintIcon;

  return (
    <Button
      accessibilityLabel={label}
      isLoading={isLoading}
      leftIcon={<Glyph />}
      onPress={() => void handleBiometricSignIn()}
      testID="biometric-sign-in-button"
      variant="secondary"
      width="100%"
    >
      {label}
    </Button>
  );
};
